import React from 'react'
import { useGetProjectTagsQuery } from '@/redux/service/project'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Skeleton } from '@/components/ui/skeleton'
import {
  AlertCircle,
  Tags,
  ChevronRight,
  ChevronDown,
  TrendingUp,
  TrendingDown,
  User2,
  Clock,
} from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { formatHours as formatHoursValue } from '@/lib/utils'
import { useCheckPermission } from '@/hooks/useCheckPermission'

interface TagWiseBreakdownProps {
  listId: number
}

const formatHours = (hours: number) => {
  const formatted = formatHoursValue(hours)
  return formatted === '-' ? '-' : `${formatted}h`
}

/** Helper to format currency values. Returns em-dash '—' if value is 0, null, or undefined. */
const formatCurrencyWithDash = (
  value: number | string | null | undefined
): string => {
  if (value === null || value === undefined || value === '') return '—'
  const num = Number(value)
  if (isNaN(num) || num === 0) return '—'
  return `$${num.toFixed(2)}`
}

/** Deterministic hue from a string */
const nameToHue = (name: string) => {
  let hash = 0
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash)
  }
  return Math.abs(hash) % 360
}

const thClass =
  'text-xs font-semibold uppercase tracking-wide text-muted-foreground whitespace-nowrap px-4'

const TagWiseBreakdown: React.FC<TagWiseBreakdownProps> = ({ listId }) => {
  const { data, isLoading, isError } = useGetProjectTagsQuery(listId, {
    skip: !listId,
  })
  const [expandedTag, setExpandedTag] = React.useState<string | null>(null)
  const { hasPageAccess } = useCheckPermission()

  const showCost = hasPageAccess('finance')
  const tags = data?.data?.tags ?? []
  const summary = data?.data

  /* ── Loading ── */
  if (isLoading) {
    return (
      <div className='border border-border rounded-xl bg-card shadow-sm overflow-hidden'>
        <div className='flex items-center gap-2 px-4 py-3 bg-muted/40 border-b border-border'>
          <Skeleton className='h-3.5 w-3.5 rounded' />
          <Skeleton className='h-3.5 w-40 rounded' />
        </div>
        <div className='p-4 space-y-3'>
          {[1, 2, 3].map(i => (
            <Skeleton key={i} className='h-12 w-full rounded-lg' />
          ))}
        </div>
      </div>
    )
  }

  /* ── Error ── */
  if (isError) {
    return (
      <div className='border border-destructive/20 bg-destructive/5 rounded-xl p-8 text-center'>
        <AlertCircle className='mx-auto mb-3 h-7 w-7 text-destructive/70' />
        <p className='text-sm font-semibold text-destructive'>
          Failed to load tag breakdown
        </p>
        <p className='text-xs text-muted-foreground mt-1'>
          Please try again later
        </p>
      </div>
    )
  }

  const overUnder = summary?.over_under_pct ?? null
  const isOver = overUnder !== null && overUnder > 0
  const isUnder = overUnder !== null && overUnder < 0

  const columnCount = showCost ? 4 : 3
  const userColumnCount = showCost ? 3 : 2

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
      className='space-y-4'
    >
      {/* ── Header ── */}
      <div className='flex items-center justify-between flex-wrap gap-3'>
        {/* Title pill */}
        <div className='flex items-center gap-2 px-3 py-1.5 rounded-lg bg-primary/10 border border-primary/20'>
          <Tags className='w-4 h-4 text-primary' />
          <span className='text-sm font-semibold text-primary'>
            Tag-wise Breakdown
          </span>
        </div>

        {/* Summary stats */}
        <div className='flex items-center gap-2 flex-wrap'>
          {summary?.estimated_hours !== undefined && (
            <div className='flex items-center gap-1.5 px-2.5 py-1 rounded-lg border border-border bg-card text-xs font-medium text-muted-foreground'>
              <Clock className='h-3 w-3 shrink-0' />
              Est.&nbsp;{formatHours(summary.estimated_hours)}
            </div>
          )}
          {summary?.total_hours !== undefined && (
            <div className='flex items-center gap-1.5 px-2.5 py-1 rounded-lg border border-border bg-card text-xs font-medium text-muted-foreground'>
              <Clock className='h-3 w-3 shrink-0 text-primary' />
              Tracked&nbsp;{formatHours(summary.total_hours)}
            </div>
          )}
          {overUnder !== null && (
            <div
              className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-xs font-semibold tabular-nums ${
                isOver
                  ? 'bg-destructive/10 text-red-500 border-red-500'
                  : isUnder
                    ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20'
                    : 'bg-muted text-muted-foreground border-border'
              }`}
            >
              {isOver ? (
                <TrendingUp className='h-3 w-3 shrink-0' />
              ) : (
                <TrendingDown className='h-3 w-3 shrink-0' />
              )}
              {isOver ? '+' : ''}
              {Number(overUnder).toFixed(2)}%
            </div>
          )}
        </div>
      </div>

      {/* ── Table card ── */}
      <div className='border border-border rounded-sm bg-card shadow-sm overflow-hidden'>
        <div className='max-h-[520px] overflow-y-auto overflow-x-auto'>
          <Table className='border-none'>
            <TableHeader>
              <TableRow className='border-b border-border bg-muted/40 hover:bg-muted/40'>
                <TableHead className='w-10 px-3' />
                <TableHead className={`${thClass} py-3`}>Tag</TableHead>
                <TableHead className={`${thClass} text-center`}>
                  Total Hours
                </TableHead>
                <TableHead className={`${thClass} text-center`}>
                  % of Total
                </TableHead>
                {showCost && (
                  <TableHead className={`${thClass} text-center`}>
                    Total Cost
                  </TableHead>
                )}
              </TableRow>
            </TableHeader>

            <TableBody>
              <AnimatePresence mode='popLayout'>
                {tags.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={columnCount + 1}
                      className='h-32 text-center'
                    >
                      <div className='flex flex-col items-center gap-1.5'>
                        <Tags className='h-7 w-7 text-muted-foreground/30' />
                        <p className='text-sm font-medium text-muted-foreground'>
                          No tag data found
                        </p>
                        <p className='text-xs text-muted-foreground/60'>
                          Time entries with tags will appear here
                        </p>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  tags.map((tagItem, index) => {
                    const isExpanded = expandedTag === tagItem.tag
                    const isUntagged = tagItem.tag.toLowerCase() === 'untagged'

                    return (
                      <React.Fragment key={tagItem.tag}>
                        {/* ── Tag row ── */}
                        <motion.tr
                          initial={{ opacity: 0, x: -8 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.04, duration: 0.25 }}
                          className='group border-b border-border/50 hover:bg-accent/30 transition-colors duration-150 cursor-pointer'
                          onClick={() =>
                            setExpandedTag(isExpanded ? null : tagItem.tag)
                          }
                        >
                          {/* Expand toggle */}
                          <TableCell className='px-3 py-3'>
                            <button
                              onClick={e => {
                                e.stopPropagation()
                                setExpandedTag(isExpanded ? null : tagItem.tag)
                              }}
                              className='p-1 rounded-md hover:bg-border transition-colors'
                            >
                              {isExpanded ? (
                                <ChevronDown className='h-3.5 w-3.5 text-primary' />
                              ) : (
                                <ChevronRight className='h-3.5 w-3.5 text-muted-foreground group-hover:text-foreground transition-colors' />
                              )}
                            </button>
                          </TableCell>

                          {/* Tag badge */}
                          <TableCell className='px-4 py-3'>
                            <span
                              className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-semibold border ${
                                isUntagged
                                  ? 'bg-muted text-muted-foreground border-border'
                                  : 'bg-primary/10 text-primary border-primary/20'
                              }`}
                            >
                              {tagItem.tag}
                            </span>
                          </TableCell>

                          {/* Hours */}
                          <TableCell className='px-4 py-3 text-center'>
                            <div className='text-sm font-semibold tabular-nums'>
                              {formatHours(tagItem.total_hours)}
                            </div>
                            <div className='flex flex-col gap-0.5 mt-0.5'>
                              <span className='text-[10px] tabular-nums text-emerald-600 dark:text-emerald-400 font-medium'>
                                {formatHours(tagItem.billable_hours)} Billable
                              </span>
                              <span className='text-[10px] tabular-nums text-red-500'>
                                {formatHours(tagItem.non_billable_hours)}{' '}
                                Non-Billable
                              </span>
                            </div>
                          </TableCell>

                          {/* % of total + progress bar */}
                          <TableCell className='px-4 py-3'>
                            <div className='flex flex-col items-center gap-1.5'>
                              <span className='text-sm font-semibold tabular-nums'>
                                {Number(tagItem.pct_of_total).toFixed(2)}%
                              </span>
                              <div className='w-20 h-1.5 bg-border rounded-full overflow-hidden'>
                                <div
                                  className='h-full bg-primary rounded-full transition-all duration-500'
                                  style={{
                                    width: `${Math.min(100, Math.max(0, tagItem.pct_of_total))}%`,
                                  }}
                                />
                              </div>
                            </div>
                          </TableCell>

                          {/* Cost */}
                          {showCost && (
                            <TableCell className='px-4 py-3 text-center'>
                              <span className='text-sm font-semibold tabular-nums'>
                                {formatCurrencyWithDash(tagItem.total_cost)}
                              </span>
                            </TableCell>
                          )}
                        </motion.tr>

                        {/* ── Expanded user drill-down ── */}
                        {isExpanded && (
                          <TableRow className='border-b border-border/50 bg-muted/20'>
                            <TableCell
                              colSpan={columnCount + 1}
                              className='px-6 py-4'
                            >
                              <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                                transition={{ duration: 0.2 }}
                                className='space-y-3'
                              >
                                <span className='text-xs font-semibold uppercase tracking-wider text-muted-foreground'>
                                  User Breakdown
                                </span>

                                <div className='border border-border rounded-lg bg-card shadow-sm overflow-hidden'>
                                  <Table>
                                    <TableHeader>
                                      <TableRow className='border-b border-border bg-muted/40 hover:bg-muted/40'>
                                        <TableHead
                                          className={`${thClass} py-2.5`}
                                        >
                                          Employee
                                        </TableHead>
                                        <TableHead
                                          className={`${thClass} text-center`}
                                        >
                                          Hours
                                        </TableHead>
                                        {showCost && (
                                          <TableHead
                                            className={`${thClass} text-center`}
                                          >
                                            Cost
                                          </TableHead>
                                        )}
                                      </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                      {tagItem.users.length > 0 ? (
                                        tagItem.users.map(user => {
                                          const hue = nameToHue(
                                            user.user_name ?? ''
                                          )
                                          return (
                                            <TableRow
                                              key={user.user_id}
                                              className='border-b border-border/50 hover:bg-accent/20 transition-colors'
                                            >
                                              {/* Employee */}
                                              <TableCell className='px-4 py-2.5'>
                                                <div className='flex items-center gap-2'>
                                                  <div
                                                    className='h-6 w-6 rounded-full flex items-center justify-center text-[9px] font-bold shrink-0 border'
                                                    style={{
                                                      backgroundColor: `hsl(${hue} 55% 88%)`,
                                                      color: `hsl(${hue} 55% 32%)`,
                                                      borderColor: `hsl(${hue} 55% 78%)`,
                                                    }}
                                                  >
                                                    {user.user_name ? (
                                                      user.user_name
                                                        .split(' ')
                                                        .map(n => n[0])
                                                        .join('')
                                                    ) : (
                                                      <User2 className='h-3 w-3' />
                                                    )}
                                                  </div>
                                                  <span className='text-sm font-medium'>
                                                    {user.user_name}
                                                  </span>
                                                </div>
                                              </TableCell>

                                              {/* Hours */}
                                              <TableCell className='px-4 py-2.5 text-center'>
                                                <div className='text-sm font-medium tabular-nums'>
                                                  {formatHours(
                                                    user.total_hours
                                                  )}
                                                </div>
                                                <div className='flex flex-col gap-0.5 mt-0.5'>
                                                  <span className='text-[10px] tabular-nums text-emerald-600 dark:text-emerald-400 font-medium'>
                                                    {formatHours(
                                                      user.billable_hours
                                                    )}{' '}
                                                    Billable
                                                  </span>
                                                  <span className='text-[10px] tabular-nums text-red-500'>
                                                    {formatHours(
                                                      user.non_billable_hours
                                                    )}{' '}
                                                    Non-Billable
                                                  </span>
                                                </div>
                                              </TableCell>

                                              {/* Cost */}
                                              {showCost && (
                                                <TableCell className='px-4 py-2.5 text-center'>
                                                  <span className='text-sm font-semibold tabular-nums'>
                                                    {formatCurrencyWithDash(
                                                      user.total_cost
                                                    )}
                                                  </span>
                                                </TableCell>
                                              )}
                                            </TableRow>
                                          )
                                        })
                                      ) : (
                                        <TableRow>
                                          <TableCell
                                            colSpan={userColumnCount}
                                            className='h-16 text-center'
                                          >
                                            <p className='text-xs text-muted-foreground'>
                                              No user logs for this tag
                                            </p>
                                          </TableCell>
                                        </TableRow>
                                      )}
                                    </TableBody>
                                  </Table>
                                </div>
                              </motion.div>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    )
                  })
                )}
              </AnimatePresence>
            </TableBody>
          </Table>
        </div>
      </div>
    </motion.div>
  )
}

export default TagWiseBreakdown
